import { Box, Center, ScrollArea, Skeleton, Text, ThemeIcon, Timeline } from '@mantine/core';
import { IconHistory } from '@tabler/icons-react';
import { useIsFetching } from '@tanstack/react-query';
import { Panel } from 'components/Panel';
import { useProfile } from 'init/use-enforce-profile';
import { tKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { type JSX, useEffect, useRef, useState } from 'react';
import { isEmpty, last, times } from 'remeda';

import { type AutomationLog, useLoadAutomationLogsInfinite } from '../../../../api';
import type { AutomationActionType } from '../../../AutomationWizard';
import { getActionTypeIcons } from '../../../AutomationWizard/components/ActionCard/ChooseActionType';

import {
  AssignAccountManagerItem,
  AssignRecoveryPlanItem,
  CopyFieldToFieldItem,
  CopyValueToFieldItem,
  PauseItem,
  UnpauseItem,
} from './components';

type HistoryPanelProps = {
  automationId: string;
  refHeight: number;
};

export const HistoryPanel = ({ automationId, refHeight }: HistoryPanelProps) => {
  const { t } = useTranslation();
  const profile = useProfile();

  const scrollableRef = useRef<HTMLDivElement>(null);

  const {
    automationLogs: { pages },
    fetchNextAutomationLogs,
    isAutomationLogsFetching,
    hasAutomationLogsNextPage,
    isAutomationLogsInitialLoading,
  } = useLoadAutomationLogsInfinite({
    automationId,
    pageLimit: profile.preferences.itemsPerPage,
  });
  const isAutomationLoading = useIsFetching({ queryKey: ['automation', automationId] });
  const isRecoveryPlanLoading = useIsFetching({ queryKey: ['lightRecoveryPlans'] });

  const [hasReachedBottom, setHasReachedBottom] = useState(false);

  const allLogs = pages.flatMap(page => page.data);

  // We're checking if everything is correctly loaded so we set the size correctly of our panel
  // preventing a flash of content when the panel is resized
  const shouldDisplayLoading =
    refHeight === 0 ||
    isAutomationLogsInitialLoading ||
    isAutomationLoading ||
    isRecoveryPlanLoading;

  useEffect(() => {
    if (hasReachedBottom && !isAutomationLogsInitialLoading) {
      if (hasAutomationLogsNextPage) {
        fetchNextAutomationLogs();
      }
    }
  }, [hasReachedBottom, fetchNextAutomationLogs]);

  if (shouldDisplayLoading) {
    return (
      <Panel h="100%" withBorder title={t(tKeys.AUTOMATION.DETAIL.HISTORY_TITLE)}>
        <Timeline color="transparent" mt="md" lineWidth={3} bulletSize={40}>
          {times(4, index => (
            <Timeline.Item bullet={<Skeleton height="100%" circle />} key={index}>
              <Box p={3}>
                <Skeleton h={12} />
                <Skeleton h={12} mt={6} width="70%" />
                <Skeleton h={12} mt={6} width="30%" />
              </Box>
            </Timeline.Item>
          ))}
        </Timeline>
      </Panel>
    );
  }

  if (isEmpty(allLogs) && !isAutomationLogsInitialLoading) {
    return (
      <Panel h="100%" withBorder title={t(tKeys.AUTOMATION.DETAIL.HISTORY_TITLE)}>
        <Center h="100%" style={{ flexDirection: 'column' }}>
          <IconHistory size={64} stroke={1.5} />
          <Text size="md" mt="md" c="dimmed">
            {t(tKeys.AUTOMATION.DETAIL.NO_HISTORY_AVAILABLE)}
          </Text>
        </Center>
      </Panel>
    );
  }
  return (
    <Panel h="100%" withBorder title={t(tKeys.AUTOMATION.DETAIL.HISTORY_TITLE)}>
      <ScrollArea
        viewportRef={scrollableRef}
        onScrollPositionChange={({ y }) => {
          const el = scrollableRef.current;
          if (!el) return;

          const { scrollHeight, clientHeight } = el;
          setHasReachedBottom(y + clientHeight >= scrollHeight - 1);
        }}
      >
        <Timeline color="gray.2" mt="md" lineWidth={3} bulletSize={40}>
          {allLogs.map((log, index) => (
            <Timeline.Item bullet={renderActionTypeIcons(log)} key={index}>
              {actionTypeToText(log)}
            </Timeline.Item>
          ))}
          {isAutomationLogsFetching && (
            <Timeline.Item bullet={<Skeleton height={40} radius="xl" />}>
              <Skeleton h={8} />
              <Skeleton h={8} mt={6} width="70%" />
            </Timeline.Item>
          )}
        </Timeline>
      </ScrollArea>
    </Panel>
  );
};

function actionTypeToText(log: AutomationLog): JSX.Element {
  const actionMap: Record<AutomationActionType, JSX.Element> = {
    // @ts-expect-error
    CopyValueToField: <CopyValueToFieldItem log={log} />,
    // @ts-expect-error
    CopyFieldToField: <CopyFieldToFieldItem log={log} />,
    // @ts-expect-error
    AssignAccountManager: <AssignAccountManagerItem log={log} />,
    AssignRecoveryPlan: <AssignRecoveryPlanItem log={log} />,
    Pause: <PauseItem log={log} />,
    Unpause: <UnpauseItem log={log} />,
  };
  return actionMap[log.actionType];
}

function renderActionTypeIcons(log: AutomationLog) {
  const actionTypeIcons = getActionTypeIcons(log.model);
  const iconConfig = last(actionTypeIcons[log.actionType]);
  const icons = Array.isArray(iconConfig) ? iconConfig : [iconConfig];

  return icons.map((iconItem, index) => {
    const IconComponent = iconItem.icon;
    return (
      <ThemeIcon key={index} size={40} radius="xl" bg="purple.2" c="purple.6">
        <IconComponent stroke={1.25} style={iconItem.style} />
      </ThemeIcon>
    );
  });
}
